import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { Link } from 'raviger';
import { Card, Col, Collapse, ListGroup, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faChevronCircleDown, faChevronCircleRight, faDoorOpen, faEdit, faPlusSquare,
} from '@fortawesome/free-solid-svg-icons';
import { faArrowDownToSquare } from '@fortawesome/pro-regular-svg-icons';
import Moment from 'react-moment';
import Header from '../components/Header';
import ShelterlyPrintifyButton from '../components/ShelterlyPrintifyButton';
import ActionsDropdown from '../components/ActionsDropdown';
import LoadingLink from '../components/LoadingLink';
import { SystemErrorContext } from '../components/SystemError';
import { ITEMS_PER_PAGE } from '.././constants';
import Paginator from '../components/Paginator';

function ShelterDetails({ id, incident, organization }) {

  const { setShowSystemError } = useContext(SystemErrorContext);

  const [showHistory, setShowHistory] = useState(false);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const [data, setData] = useState({
    name: '',
    address: '',
    full_address: '',
    city: '',
    state: '',
    zip_code: '',
    description: '',
    image: '',
    phone: '',
    display_phone: '',
    buildings: [],
    unroomed_animals: [],
    intake_summaries: [],
    animal_count: 0,
  });

  const handleDownloadSinglePagePdfClick = async () => {
    setIsLoading(true);
    // wait for 1 tick so spinner will set before the print button locks up the browser
    await new Promise(resolve => setTimeout(resolve, 0));
   window.open('https://shelterly-files.s3.us-west-2.amazonaws.com/public/ShelterlyIntakeForm1P.pdf', '_blank', 'noopener,noreferrer');
   setIsLoading(false);
  }

  const handleDownloadTwoPagePdfClick = async () => {
    setIsLoading(true);
    // wait for 1 tick so spinner will set before the print button locks up the browser
    await new Promise(resolve => setTimeout(resolve, 0));
   window.open('https://shelterly-files.s3.us-west-2.amazonaws.com/public/ShelterlyIntakeForm2P.pdf', '_blank', 'noopener,noreferrer');
   setIsLoading(false);
  }

  useEffect(() => {
    setNumPages(Math.ceil(data.intake_summaries.length / ITEMS_PER_PAGE))
  }, [data.intake_summaries]);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchShelterData = async () => {
      // Fetch Shelter Details data.
      await axios.get('/shelter/api/shelter/' + id + '/?incident=' + incident + '&organization=' + organization, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let count = 0;
          response.data.unroomed_animals.forEach(unroomed_animal => {
            count = count + unroomed_animal.animal_count;
          });
          response.data['unroomed_count'] = count;
          setData(response.data);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      })
      .finally(() => setIsLoading(false));
    };
    fetchShelterData();
    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, incident]);

  return (
    <>
      <Header>
        {data.name} -&nbsp;
        Shelter Details
      </Header>
      <Row className="d-flex">
        <Col>
          <Card className="border rounded d-flex" style={{width:"100%", height: "100%"}}>
            <Card.Body>
              <Card.Title>
                <h4>Information</h4>
              </Card.Title>
              <p className="phr" />
              <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
                <ListGroup.Item>
                  <b>Address:</b> {data.full_address}
                </ListGroup.Item>
                <ListGroup.Item>
                  <b>Phone:</b> {data.display_phone || "No contact number listed"}
                </ListGroup.Item>
                <ListGroup.Item>
                  <b>Currently Sheltering:</b> {data.animal_count || 0} Animal{data.animal_count === 1 ? "" : "s"}
                </ListGroup.Item>
                <ListGroup.Item>
                  <b>Description: </b>{data.description || "None"}
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
        <Col className="d-flex flex-column ps-0">
          <Card className="border rounded d-flex ps-0" style={{width:"100%", height:"100%"}}>
            <Card.Body>
              <Card.Title>
                <h4>Intake
                  {isLoading ? (
                    <Spinner
                      className="align-self-center me-3 float-end"
                      {...{
                        as: 'span',
                        animation: 'border',
                        size: undefined,
                        role: 'status',
                        'aria-hidden': 'true',
                        variant: 'light',
                        style: {
                          height: '1.5rem',
                          width: '1.5rem',
                          marginBottom: '0.75rem'
                        }
                      }}
                    />
                  ) : (
                    <ActionsDropdown variant="secondary" title="Actions" className="float-end" style={{marginTop:"-4px"}}>
                      <ShelterlyPrintifyButton
                        id="shelter-intake-1p-form"
                        spinnerSize={2.0}
                        tooltipPlacement='right'
                        label='Download Shelter 1-Page Intake Form'
                        printFunc={handleDownloadSinglePagePdfClick}
                        disabled={isLoading}
                        noOverlay={true}
                      />
                      <ShelterlyPrintifyButton
                        id="shelter-intake-2p-form"
                        spinnerSize={2.0}
                        tooltipPlacement='right'
                        label='Download Shelter 2-Page Intake Form'
                        printFunc={handleDownloadTwoPagePdfClick}
                        disabled={isLoading}
                        noOverlay={true}
                      />
                    </ActionsDropdown>
                  )}
                </h4>
              </Card.Title>
              <p className="phr" />
              <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
                <ListGroup.Item className="rounded" action><Link href={"/" + organization + "/" + incident + "/intake/workflow/owner?shelter_id=" + id} style={{color:"#FFF"}}><FontAwesomeIcon icon={faDoorOpen} inverse/> <b>Intake from Walk-In (Owner)</b></Link></ListGroup.Item>
                <ListGroup.Item className="rounded" action><Link href={"/" + organization + "/" + incident + "/intake/workflow/reporter?shelter_id=" + id} style={{color:"#FFF"}}><FontAwesomeIcon icon={faDoorOpen} inverse/> <b>Intake from Walk-In (Non-Owner)</b></Link></ListGroup.Item>
                <ListGroup.Item className="rounded" action><Link href={"/" + organization + "/" + incident + "/intake/workflow/field_member?shelter_id=" + id} style={{color:"#FFF"}}><FontAwesomeIcon icon={faDoorOpen} inverse/> <b>Intake from Walk-In (Field Team Member)</b></Link></ListGroup.Item>
                <ListGroup.Item className="rounded" action><Link href={"/" + organization + "/" + incident + "/shelter/" + id + "/intake"} style={{color:"#FFF"}}><FontAwesomeIcon icon={faDoorOpen} inverse/> <b>Intake from Field Assignment</b></Link></ListGroup.Item>
                <ListGroup.Item>
                  <b>Roomless:</b> {data.unroomed_count} Animal{data.unroomed_count === 1 ? "" : "s"}
                  <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-assign`}>Assign animals to rooms</Tooltip>}>
                    <Link href={"/" + organization + "/" + incident + "/shelter/" + id + "/assign"}><FontAwesomeIcon icon={faArrowDownToSquare} size="lg" className="ms-1 fa-move-up" inverse /></Link>
                  </OverlayTrigger>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Card className="border rounded d-flex mt-3">
        <Card.Body style={{marginBottom:"-20px"}}>
          <Card.Title>
            <h4 className="mb-0">Buildings ({data.buildings.length})
              {isLoading ? (
                <Spinner
                  className="align-self-center me-3 float-end"
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '1.5rem',
                      width: '1.5rem',
                      marginBottom: '0.75rem'
                    }
                  }}
                />
              ) : (
                <ActionsDropdown variant="secondary" title="Actions" className="float-end" style={{marginTop:"-4px"}}>
                  <LoadingLink href={`/${organization}/${incident}/shelter/${id}/building/new`} isLoading={isLoading} className="d-block" label="Add a Building">
                    <FontAwesomeIcon icon={faPlusSquare} className="fas" style={{cursor:'pointer'}} inverse />
                  </LoadingLink>
                </ActionsDropdown>
              )}
            </h4>
          </Card.Title>
          <p className="phr" />
          <span className="d-flex flex-wrap ms-0">
          {data.buildings.map(building => (
            <span key={building.id} className="ps-0 pe-0 me-3 mb-3">
              <Link href={"/" + organization + "/" + incident + "/shelter/building/" + building.id} className="building-link" style={{textDecoration:"none", color:"white"}}>
                <Card className="border rounded shelter-hover-div" style={{minWidth:"315px", maxWidth:"315px", whiteSpace:"nowrap", overflow:"hidden"}}>
                  <div className="row no-gutters hover-div ms-0" style={{textTransform:"capitalize", marginRight:"-2px"}}>
                    <Row className="ms-0 me-0 w-100 p-0" style={{flexWrap:"nowrap"}}>
                      <div className="border-end p-0" style={{width:"100px", minWidth:"100px"}}>
                        <FontAwesomeIcon icon={faBuilding} size="5x" className="building-icon" style={{marginLeft:"17px", marginTop:"6px"}} inverse />
                      </div>
                      <Col className="p-0" style={{marginLeft:"10px", marginRight:"-10px"}}>
                        <div className="border" title={building.name} style={{paddingTop:"5px", paddingBottom:"7px", paddingLeft:"8px", marginLeft:"-11px", marginRight:"-5px", marginTop:"-1px", width:"100%", fontSize:"18px", backgroundColor:"#615e5e", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{building.name}</div>
                        <div style={{marginTop:"2px"}}>
                          {building.rooms.length} room{building.rooms.length !== 1 ? "s" : ""}
                        </div>
                        <div>
                          {building.animal_count||0} animal{building.animal_count !== 1 ? "s" : ""}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Card>
              </Link>
            </span>
          ))}
          {data.buildings.length < 1 ? <p>No buildings have been created yet.</p> : ""}
          </span>
        </Card.Body>
      </Card>
      {/* <History action_history={data.action_history} /> */}
      {data.intake_summaries.length > 0 ? <p className="phr" /> : ""}
      {data.intake_summaries.length > 0 ? <h2 className="mb-3">Intake History<FontAwesomeIcon icon={faChevronCircleRight} hidden={showHistory} onClick={() => setShowHistory(!showHistory)} className="ms-2" style={{verticalAlign:"middle"}} inverse /><FontAwesomeIcon icon={faChevronCircleDown} hidden={!showHistory} onClick={() => setShowHistory(!showHistory)} className="ms-2" style={{verticalAlign:"middle"}} inverse /></h2> : ""}
      {data.intake_summaries.map((intakesummary, index) => (
        <Collapse key={intakesummary.id} in={showHistory} hidden={page !== Math.ceil((index+1)/ITEMS_PER_PAGE)}>
          <div>
            <Card className="border rounded d-flex mb-2">
              <Card.Body>
              {intakesummary.intake_type === 'owners_walkin' ? 'Owner Walk-In ' : intakesummary.intake_type === 'reporter_walkin' ? 'Reporter Walk-In ' : 'Field '}<Link href={"/" + organization + "/" + incident + "/shelter/intakesummary/" + intakesummary.id} className="text-link" style={{textDecoration:"none", color:"white"}}>Intake Summary #{intakesummary.id}</Link> for {intakesummary.animal_count} animal{intakesummary.animal_count === 1 ? '' : 's'} on <Moment format="MMMM Do YYYY">{intakesummary.date}</Moment> at <Moment format="HH:mm">{intakesummary.date}</Moment>.
              </Card.Body>
            </Card>
          </div>
        </Collapse>
      ))}
      <Paginator page={page} numPages={numPages} setPage={setPage} />
    </>
  );
};

export default ShelterDetails;
